import { createContext, useContext } from 'react'
import { defaultFormatter } from './formatters'

/**
 * Recharts 3 renders tooltip content in a portal. Passing a new `content` function
 * every render or relying only on cloneElement can drop series metadata. Provider +
 * stable `content={StableRechartsChartTooltip}` keeps series/formatters available.
 */
export const RechartsChartTooltipSeriesContext = createContext(null)
export const RechartsChartTooltipOptionsContext = createContext(null)

export function RechartsChartTooltipProvider({ series, options = {}, children }) {
  return (
    <RechartsChartTooltipSeriesContext.Provider value={series}>
      <RechartsChartTooltipOptionsContext.Provider value={options}>
        {children}
      </RechartsChartTooltipOptionsContext.Provider>
    </RechartsChartTooltipSeriesContext.Provider>
  )
}

/**
 * Use as: <Tooltip content={StableRechartsChartTooltip} />
 * (pass the function reference, do not invoke). Pair with RechartsChartTooltipProvider.
 */
export function StableRechartsChartTooltip(tooltipProps) {
  const ctxSeries = useContext(RechartsChartTooltipSeriesContext)
  const ctxOptions = useContext(RechartsChartTooltipOptionsContext) || {}

  const fromProps = tooltipProps?.chartSeries ?? tooltipProps?.series
  const chartSeries = Array.isArray(fromProps) ? fromProps : Array.isArray(ctxSeries) ? ctxSeries : []

  return (
    <ChartTooltip
      active={tooltipProps.active}
      payload={tooltipProps.payload}
      label={tooltipProps.label}
      chartSeries={chartSeries}
      labelFormatter={tooltipProps.labelFormatter ?? ctxOptions.labelFormatter}
      valueFormatter={tooltipProps.valueFormatter ?? ctxOptions.valueFormatter}
      extraContent={tooltipProps.extraContent ?? ctxOptions.extraContent}
      className={[tooltipProps.className, ctxOptions.className].filter(Boolean).join(' ')}
    />
  )
}

/** @deprecated Prefer RechartsChartTooltipProvider + StableRechartsChartTooltip */
export function chartTooltipRender(chartSeries, options = {}) {
  const { labelFormatter, valueFormatter, extraContent, className } = options
  return function RechartsChartTooltipHost(tooltipProps) {
    return (
      <ChartTooltip
        {...tooltipProps}
        chartSeries={chartSeries}
        labelFormatter={labelFormatter}
        valueFormatter={valueFormatter}
        extraContent={extraContent}
        className={className ?? ''}
      />
    )
  }
}

function buildSeriesLookup(series) {
  const map = new Map()
  ;(series || []).forEach((item) => {
    if (!item) return
    if (item.key != null) {
      map.set(item.key, item)
      map.set(String(item.key), item)
    }
    if (item.label != null && item.label !== item.key) {
      map.set(item.label, item)
      map.set(String(item.label), item)
    }
  })
  return map
}

function resolveTooltipEntryValue(entry, config, allSeries) {
  if (entry == null) return undefined
  const fromVal = entry.value
  if (fromVal !== undefined && fromVal !== null && fromVal !== '') return fromVal

  const row = entry.payload
  const nested = row && typeof row === 'object' && row.payload && typeof row.payload === 'object' ? row.payload : null
  const sources = [row, nested].filter((s) => s && typeof s === 'object' && !Array.isArray(s))

  const keysToTry = [entry.dataKey, config?.key].filter((k) => k != null && typeof k !== 'function')

  for (const source of sources) {
    for (const key of keysToTry) {
      if (key != null && Object.prototype.hasOwnProperty.call(source, key) && source[key] !== undefined) {
        return source[key]
      }
      const sk = String(key)
      if (Object.prototype.hasOwnProperty.call(source, sk) && source[sk] !== undefined) return source[sk]
    }
  }

  if (config?.key != null && typeof config.key !== 'function' && typeof entry === 'object') {
    if (Object.prototype.hasOwnProperty.call(entry, config.key) && entry[config.key] !== undefined) {
      return entry[config.key]
    }
  }

  for (const source of sources) {
    for (const s of allSeries || []) {
      if (!s?.key || typeof s.key === 'function') continue
      const k = s.key
      if (Object.prototype.hasOwnProperty.call(source, k) && source[k] !== undefined) return source[k]
    }
  }
  return undefined
}

function safeFormat(formatter, raw) {
  try {
    const fmt = typeof formatter === 'function' ? formatter : defaultFormatter
    const out = fmt(raw)
    if (out === null || out === undefined) return '—'
    return out
  } catch (_e) {
    return raw !== undefined && raw !== null ? String(raw) : '—'
  }
}

const ChartTooltip = ({
  active,
  payload: payloadIn,
  label,
  chartSeries,
  series: seriesProp,
  labelFormatter,
  valueFormatter,
  extraContent,
  className = '',
}) => {
  const payload = Array.isArray(payloadIn) ? payloadIn : []
  if (!active || payload.length === 0) return null

  const series = Array.isArray(chartSeries)
    ? chartSeries
    : Array.isArray(seriesProp)
      ? seriesProp
      : []

  const seriesMap = buildSeriesLookup(series)
  const title = labelFormatter ? labelFormatter(label) : label

  return (
    <div className={`rg-chart__tooltip ${className}`.trim()}>
      {title && <div className="rg-chart__tooltip-title">{title}</div>}
      {payload.map((entry, index) => {
        const dataKeyStr = entry.dataKey != null ? String(entry.dataKey) : ''
        const nameStr = entry.name != null ? String(entry.name) : ''
        const config =
          (entry.dataKey != null ? seriesMap.get(entry.dataKey) || seriesMap.get(dataKeyStr) : null) ||
          (entry.name != null ? seriesMap.get(entry.name) || seriesMap.get(nameStr) : null)
        const raw = resolveTooltipEntryValue(entry, config, series)
        if (!config) {
          const displayValue = safeFormat(valueFormatter || defaultFormatter, raw)
          const rowLabel = nameStr || dataKeyStr || 'Value'
          return (
            <div key={`raw-${index}-${dataKeyStr || nameStr}`} className="rg-chart__tooltip-row">
              <span className="rg-chart__tooltip-rowLabel">{rowLabel}</span>
              <strong className="rg-chart__tooltip-value">{displayValue}</strong>
            </div>
          )
        }
        const formatter =
          typeof config.formatter === 'function' ? config.formatter : valueFormatter || defaultFormatter
        const displayValue = safeFormat(formatter, raw)
        const rowKey = entry.dataKey != null ? String(entry.dataKey) : String(config.key)
        return (
          <div key={`${rowKey}-${config.key}-${index}`} className="rg-chart__tooltip-row">
            <span className="rg-chart__tooltip-rowLabel">
              <span
                className="rg-chart__tooltip-dot"
                style={{ backgroundColor: `var(${config?.colorVar || '--chart-1'})` }}
              />
              {config?.label || entry.name}
            </span>
            <strong className="rg-chart__tooltip-value">{displayValue}</strong>
          </div>
        )
      })}
      {extraContent
        ? (() => {
            try {
              return extraContent({ label, payload })
            } catch (_e) {
              return null
            }
          })()
        : null}
    </div>
  )
}

export default ChartTooltip
